/**
 * Modules Contracts
 * Zod schemas for modules API endpoints.
 * @module api/contracts/modules
 */
import { z } from "zod"

// LLM:METADATA
// :topology: [Editor SPA | API Contracts | Modules]
// :rationale: "Provides type-safe validation for Modules CRUD endpoints - enables contract-based testing and runtime validation"
// :contract: pre: "Valid input data for each operation", post: "Validated response matching backend contract"
// :references: SPEC: "Phase 3.1 Contract Completion"
// LLM:END

// Module Field Schema
// STRICT RAW contract for module field definitions from DB
// Required fields: id, name, type (always present from DB)
// Note: PHP mysqli returns integer IDs as strings - this is natural backend behavior
export const ModuleFieldSchema = z.object({
  id: z.number(), // DB integer ID - REQUIRED
  name: z.string(), // Field identifier - REQUIRED
  type: z.string(), // Field type (string, text, number, etc.) - REQUIRED
  label: z.string().nullable().optional(), // Display label - legitimately optional
  // Backend returns required as: boolean (true/false), number (0/1), OR string ("1"/"0")
  // This union reflects ACTUAL backend behavior, not ideal types
  // TODO: Normalize backend to always return number for boolean flags
  required: z
    .union([z.boolean(), z.number(), z.string()])
    .nullable()
    .optional(),
  // Field options - record structure or null
  options: z.record(z.unknown()).nullable().optional(),
  // Default value - can be any type or null
  default: z.unknown().nullable().optional(),
  // Display/presentation flags
  show_in_list: z.union([z.number(), z.string()]).nullable().optional(), // 0/1 flag (backend may return string)
  visible: z.union([z.number(), z.string()]).nullable().optional(), // 0/1 flag (backend may return string)
  ordernum: z.union([z.number(), z.string()]).nullable().optional(), // Display order (backend may return string)
  hint: z.string().nullable().optional(), // Help text
  module_link_name: z.string().optional(), // Related module name for link fields
})

// Module Item Schema (for items list)
// STRICT RAW contract for module items from DB
// Required field: id (DB entity ID - always present)
// Other fields are legitimately optional (not always returned in list views)
export const ModuleItemSchema = z.object({
  id: z.number(), // DB integer ID - REQUIRED
  name: z.string().nullable().optional(), // Item name - optional in partial responses
  active: z.number().nullable().optional(), // Active status (0/1) - optional
  order_num: z.number().nullable().optional(), // Display order - optional
  created_at: z.string().nullable().optional(), // ISO timestamp - optional
  updated_at: z.string().nullable().optional(), // ISO timestamp - optional
})

// Module Schema
export const ModuleSchema = z.object({
  id: z.number(),
  name: z.string(),
  table_name: z.string().nullable(),
  active: z.number().nullable(),
  description: z.string().nullable(),
  icon: z.string().nullable(),
  order_num: z.number().nullable(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable(),
})

// Module Menu Item Schema
// STRICT RAW contract for menu items
export const ModuleMenuItemSchema = z.object({
  title: z.string(), // Menu item title - REQUIRED (meaningless without it)
  url: z.string(), // Menu item URL/path - REQUIRED (meaningless without it)
})

// Modules List Item Schema
// STRICT RAW contract for individual module in list
// At least one identifier (module_id or id) is required
export const ModulesListItemSchema = z
  .object({
    module_id: z.number().optional(), // Module ID (alternative field)
    id: z.number().optional(), // Module ID (primary field)
    module_name: z.string().optional(), // Module name - optional in different response formats
    name: z.string().optional(), // Module name (alternative field) - optional
  })
  .refine((entry) => entry.module_id != null || entry.id != null, {
    message: "Module entry must include module_id or id",
  })

// Modules List Response - handles both populated and empty responses
// STRICT RAW contract for modules list endpoint
export const ModulesListResponseSchema = z
  .object({
    modules: z.array(ModulesListItemSchema).optional(), // Array of modules
    menuItems: z.array(ModuleMenuItemSchema).optional(), // Array of menu items
  })
  // Transform to ensure we always have arrays (normalization happens here)
  .transform((val) => ({
    modules: val.modules ?? [],
    menuItems: val.menuItems ?? [],
  }))

export const ModulesListResponseSchemaLegacy = z
  .array(ModulesListItemSchema)
  .min(0)

export const ModulesListAnyResponseSchema = z.union([
  ModulesListResponseSchema,
  ModulesListResponseSchemaLegacy,
])

// Module Items Response - simple array format
export const ModuleItemsResponseSchema = z.array(ModuleItemSchema)

// Module Items Paginated Response
// Backend returns {items: [], total: number, debug_filters?: {}}
// Used by /api/modules/items/:id endpoint
// STRICT RAW contract for paginated module items
export const ModuleItemsPaginatedResponseSchema = z.object({
  items: z
    .array(
      z
        .object({
          m_item_id: z.number(), // Item ID - REQUIRED
          m_item_title: z.string().optional(), // Item title - optional
          m_item_visible: z.number().optional(), // Visibility flag - optional
        })
        // Keep passthrough() for dynamic module fields (m_item_*)
        .passthrough()
    )
    .default([]),
  total: z.number(), // Total count - REQUIRED
  debug_filters: z
    .union([z.record(z.unknown()), z.array(z.unknown())])
    .optional(), // Debug info can be object or array
  page: z.number().optional(), // Current page - optional
  per_page: z.number().optional(), // Items per page - optional
})

// LP-specific RAW contract for /api/modules/items/115.
// label_name is a required response key: it may be a string or null, but it
// must not be omitted or supplied as a numeric/object alias.
export const LpModuleItemsPaginatedResponseSchema = z.object({
  items: z
    .array(
      z
        .object({
          m_item_id: z.number(),
          m_item_label: z.number(),
          label_name: z.string().nullable(),
        })
        .passthrough()
    )
    .default([]),
  total: z.number(),
  debug_filters: z
    .union([z.record(z.unknown()), z.array(z.unknown())])
    .optional(),
  page: z.number().optional(),
  per_page: z.number().optional(),
})

// Module Fields Response
export const ModuleFieldsResponseSchema = z.array(ModuleFieldSchema)

// Single Module Response
export const ModuleResponseSchema = ModuleSchema

// Module Create Request
export const ModuleCreateRequestSchema = z.object({
  name: z.string().min(1, "Name is required"),
  table_name: z.string().optional(),
  active: z.number().optional().default(1),
  description: z.string().optional(),
  icon: z.string().optional(),
  order_num: z.number().optional(),
})

// Module Update Request
export const ModuleUpdateRequestSchema = ModuleCreateRequestSchema.partial()

// Module Delete Response
export const ModuleDeleteResponseSchema = z.object({
  result: z.string(),
  message: z.string().optional(),
})

// Module Duplicate Response
export const ModuleDuplicateResponseSchema = z.object({
  result: z.string(),
  message: z.string().optional(),
  module_id: z.number().optional(),
})

// Module Export Response
export const ModuleExportResponseSchema = z.object({
  result: z.string(),
  data: z.unknown().optional(),
  filename: z.string().optional(),
  message: z.string().optional(),
})

// Type exports
export const ModulesListResponse = ModulesListResponseSchema
export const ModuleItemsResponse = ModuleItemsResponseSchema
export const ModuleFieldsResponse = ModuleFieldsResponseSchema
export const ModuleResponse = ModuleResponseSchema
export const ModuleCreateRequest = ModuleCreateRequestSchema
export const ModuleUpdateRequest = ModuleUpdateRequestSchema
export const ModuleDeleteResponse = ModuleDeleteResponseSchema
export const ModuleDuplicateResponse = ModuleDuplicateResponseSchema
export const ModuleExportResponse = ModuleExportResponseSchema

// Combined contract object for easy import
export const modulesContract = {
  list: {
    schema: ModulesListAnyResponseSchema,
    endpoint: "modules.list",
  },
  get: {
    schema: ModuleResponseSchema,
    endpoint: "modules.get",
  },
  create: {
    request: ModuleCreateRequestSchema,
    response: ModuleResponseSchema,
    endpoint: "modules.create",
  },
  update: {
    request: ModuleUpdateRequestSchema,
    response: ModuleResponseSchema,
    endpoint: "modules.update",
  },
  delete: {
    schema: ModuleDeleteResponseSchema,
    endpoint: "modules.delete",
  },
  duplicate: {
    schema: ModuleDuplicateResponseSchema,
    endpoint: "modules.duplicate",
  },
  export: {
    schema: ModuleExportResponseSchema,
    endpoint: "modules.export",
  },
  // Legacy /edit/new_api endpoints
  legacy: {
    fields: {
      schema: ModuleFieldsResponseSchema,
      endpoint: "modules.legacy.fields",
    },
    get: {
      schema: ModuleItemSchema,
      endpoint: "modules.legacy.get",
    },
  },
}

// Additional schemas for specific endpoints
// Used for /api/modules/:id/get/:itemId and /edit/new_api/modules/:id/get/:itemId
// STRICT RAW contract for single module item response
// Note: common is z.record(z.unknown()) to allow dynamic module fields
export const ModuleGetItemResponseSchema = z.object({
  itemInfo: z.object({
    // Common fields - dynamic structure based on module definition
    // Using record because module fields are not statically known
    common: z.record(z.unknown()),
  }),
})

// Used for /edit/new_api/modules/:id/get/:itemId (legacy)
export const LegacyModuleItemResponseSchema = ModuleGetItemResponseSchema

// Used for /api/modules/items/:id endpoint (paginated)
export const ModuleItemsByIdResponseSchema = ModuleItemsPaginatedResponseSchema

// Used for /edit/new_api/modules/:id/items (legacy paginated)
export const LegacyModuleItemsResponseSchema =
  ModuleItemsPaginatedResponseSchema

// Type exports for additional schemas
export const ModuleGetItemResponse = ModuleGetItemResponseSchema
export const LegacyModuleItemResponse = LegacyModuleItemResponseSchema
export const ModuleItemsByIdResponse = ModuleItemsByIdResponseSchema
export const LegacyModuleItemsResponse = LegacyModuleItemsResponseSchema
export const ModulesListAnyResponse = ModulesListAnyResponseSchema

// ==========================================================================
// Additional RAW schemas for catalog client endpoints
// ==========================================================================

// Single module item response (for GET /catalog/modules/:id/items/:itemId)
export const ModuleItemResponseSchema = ModuleItemSchema

// Module Item Create/Update Response
// Backend returns the created/updated item with id and timestamps
// STRICT RAW contract for item operations (create/update)
export const ModuleItemOperationResponseSchema = z.object({
  id: z.number(), // Created/updated item ID - REQUIRED (returned as string by PHP mysqli)
  name: z.string().nullable().optional(), // Item name - optional
  active: z.number().nullable().optional(), // Active status - optional
  order_num: z.number().nullable().optional(), // Display order - optional
  created_at: z.string().nullable().optional(), // Creation timestamp - optional
  updated_at: z.string().nullable().optional(), // Update timestamp - optional
})

// Toggle/Bulk operation response (same as delete response)
export const ModuleToggleResponseSchema = z.object({
  result: z.string(),
  message: z.string().optional(),
})

// Refresh items response (returns array of items)
export const ModuleRefreshResponseSchema = z.array(ModuleItemSchema)

// ==========================================================================
// Request Schemas for Catalog Client Operations
// ==========================================================================

// PUT /catalog/modules/:id/items - Toggle visibility request
export const ModuleItemsToggleRequestSchema = z.object({
  ids: z
    .array(z.number())
    .min(1, "At least one item ID required for toggle operation"),
  active: z.number(), // Active status to set (0 or 1)
})

// PUT /catalog/modules/:id/items - Refresh/reorder request
export const ModuleItemRefreshItemSchema = z.object({
  id: z.number(), // Item ID - REQUIRED (client sends number)
  order_num: z.number(), // New order position - REQUIRED
})

export const ModuleItemsRefreshRequestSchema = z.object({
  items: z
    .array(ModuleItemRefreshItemSchema)
    .min(1, "At least one item required for refresh operation"),
})

// Contract exports for catalog client
export const catalogModulesContract = {
  // List all modules
  list: {
    schema: ModulesListAnyResponseSchema,
    endpoint: "catalog.modules.list",
  },
  // Get module fields
  fields: {
    schema: ModuleFieldsResponseSchema,
    endpoint: "catalog.modules.fields",
  },
  // Get all items for a module
  items: {
    schema: ModuleItemsResponseSchema,
    endpoint: "catalog.modules.items",
  },
  // Get single item
  item: {
    schema: ModuleItemResponseSchema,
    endpoint: "catalog.modules.item",
  },
  // Create item
  itemCreate: {
    schema: ModuleItemOperationResponseSchema,
    endpoint: "catalog.modules.item.create",
  },
  // Update item
  itemUpdate: {
    schema: ModuleItemOperationResponseSchema,
    endpoint: "catalog.modules.item.update",
  },
  // Delete item
  itemDelete: {
    schema: ModuleDeleteResponseSchema,
    endpoint: "catalog.modules.item.delete",
  },
  // Bulk delete items
  itemsDelete: {
    schema: ModuleDeleteResponseSchema,
    endpoint: "catalog.modules.items.delete",
  },
  // Toggle items visibility
  itemsToggle: {
    schema: ModuleToggleResponseSchema,
    endpoint: "catalog.modules.items.toggle",
  },
  // Refresh/reorder items
  itemsRefresh: {
    schema: ModuleRefreshResponseSchema,
    endpoint: "catalog.modules.items.refresh",
  },
  // Duplicate module
  duplicate: {
    schema: ModuleDuplicateResponseSchema,
    endpoint: "catalog.modules.duplicate",
  },
  // Export module
  export: {
    schema: ModuleExportResponseSchema,
    endpoint: "catalog.modules.export",
  },
}
